import { supabase } from '@/utils/supabaseClient';
import { clearUserIdCache } from './userIdService';

export const signUp = async (email: string, password: string, meta: any) => {
  try {
    // Supabase auth signup - profile is created automatically by database trigger
    // The trigger (create_profile_on_signup) runs with SECURITY DEFINER to bypass RLS
    const result = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: meta,
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });

    // Handle auth signup errors
    if (result.error) {
      throw new Error(result.error.message);
    }

    return result;
  } catch (error) {
    // Return a properly formatted error
    return {
      data: { user: null, session: null },
      error: {
        message: error instanceof Error ? error.message : 'An unexpected error occurred during registration',
        code: 'SIGNUP_ERROR'
      }
    };
  }
};

export const signIn = async (email: string, password: string, rememberMe: boolean = true) => {
  try {
    const result = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (result.data.user && result.data.session) {
      // Store remember me preference
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('userEmail', email);
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('userEmail');
      }

      // Fetch user profile to get role
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', result.data.user.id)
        .single();

      if (profile) {
        // Add profile to the result (extend type with any)
        (result.data as any).profile = profile;
      }
    }

    return result;
  } catch (error) {
    throw error;
  }
};

export const signOut = async () => {
  // Check if user wants to be remembered
  const rememberMe = localStorage.getItem('rememberMe') === 'true';

  // Clear user ID cache
  clearUserIdCache();

  const result = await supabase.auth.signOut();

  // Only clear remember me data if user explicitly logged out
  if (!rememberMe) {
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('userEmail');
  }

  return result;
};

export const getUser = async () => {
  const result = await supabase.auth.getUser();
  return result;
};

export const getCurrentUserProfile = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return null;

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      return null;
    }

    return { user, profile };
  } catch {
    return null;
  }
};

export const getUserRole = async (userId: string) => {
  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .single();

    if (error) {
      return null;
    }

    return profile?.role || null;
  } catch {
    return null;
  }
};

export const getRoleBasedDashboard = (role: string) => {
  switch (role) {
    case 'admin':
      return '/admin-dashboard';
    case 'tenant':
      return '/tenant-dashboard';
    case 'landlord':
      return '/landlord-dashboard';
    default:
      return '/dashboard';
  }
};
